import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import Navbar from "@/components/Navbar";
import { api } from "@/lib/api";
import { ArrowRight, BookOpen, Lock, Sparkle, GlobeHemisphereWest } from "@phosphor-icons/react";
import { toast } from "sonner";

const WPM = 220;

function readingMinutes(w) {
  if (!w) return null;
  return Math.max(4, Math.round(w / WPM));
}

function fmtDate(s) {
  if (!s) return "";
  try {
    return new Date(s).toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" });
  } catch {
    return "";
  }
}

const CATEGORY_LABEL = {
  earth: "Earth",
  nature: "Nature",
  wildlife: "Wildlife",
  people: "People",
  climate: "Climate",
};

export default function Research() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [category, setCategory] = useState("");

  useEffect(() => {
    (async () => {
      setLoading(true);
      try {
        const q = category ? `&category=${category}` : "";
        const { data } = await api.get(`/research?limit=30${q}`);
        setItems(data.items || []);
      } catch {
        toast.error("Couldn't load research");
      } finally {
        setLoading(false);
      }
    })();
  }, [category]);

  return (
    <div className="min-h-screen bg-sand-50" data-testid="research-page">
      <Navbar />
      <main className="max-w-6xl mx-auto px-6 lg:px-10 py-14">
        <div className="text-center max-w-2xl mx-auto">
          <div className="font-mono-data text-mute inline-flex items-center gap-2 justify-center">
            <BookOpen size={14} weight="duotone" /> research imprint
          </div>
          <h1 className="font-display text-5xl md:text-6xl text-ink leading-tight mt-2">
            Rigorous briefs on <em className="italic text-terra">Earth, Nature</em> &amp; a warming world.
          </h1>
          <p className="text-mute mt-4 leading-relaxed">
            Journal-style research papers (1000–2000 words with references) on climate,
            biodiversity, wildlife, and the people who steward the land.
            Free reads are marked; the rest come with your Grower subscription.
          </p>
        </div>

        {/* Category filter */}
        <div className="mt-10 flex flex-wrap justify-center gap-2" data-testid="research-categories">
          <button
            onClick={() => setCategory("")}
            className={`px-3 py-1.5 rounded-full text-sm border transition ${!category ? "bg-forest text-white border-forest" : "bg-white text-ink border-sand-200 hover:border-forest/40"}`}
          >All</button>
          {Object.entries(CATEGORY_LABEL).map(([id, label]) => (
            <button
              key={id}
              onClick={() => setCategory(id)}
              data-testid={`research-cat-${id}`}
              className={`px-3 py-1.5 rounded-full text-sm border transition ${category === id ? "bg-forest text-white border-forest" : "bg-white text-ink border-sand-200 hover:border-forest/40"}`}
            >{label}</button>
          ))}
        </div>

        <div className="mt-10">
          {loading ? (
            <div className="font-mono-data text-mute animate-pulse-soft">loading papers…</div>
          ) : items.length === 0 ? (
            <div className="bg-sand-100 border border-sand-200 rounded-3xl p-12 text-center" data-testid="research-empty">
              <GlobeHemisphereWest size={48} weight="duotone" className="text-forest mx-auto mb-4" />
              <h3 className="font-display text-3xl">No papers yet{category ? ` in ${CATEGORY_LABEL[category]}` : ""}</h3>
              <p className="text-mute mt-2">Check back soon — new briefs land every month.</p>
            </div>
          ) : (
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6" data-testid="research-grid">
              {items.map((p) => (
                <Paper key={p.slug} paper={p} />
              ))}
            </div>
          )}
        </div>
      </main>
    </div>
  );
}

function Paper({ paper }) {
  const mins = readingMinutes(paper.word_count);
  return (
    <Link
      to={`/research/${paper.slug}`}
      data-testid={`research-card-${paper.slug}`}
      className="group block bg-sand-100 border border-sand-200 rounded-3xl overflow-hidden hover:shadow-soft transition"
    >
      <div className="aspect-[16/9] bg-ink/5 overflow-hidden relative">
        {paper.image_b64 ? (
          <img
            src={`data:image/png;base64,${paper.image_b64}`}
            alt={paper.title}
            className="w-full h-full object-cover group-hover:scale-[1.02] transition-transform duration-500"
          />
        ) : (
          <div className="w-full h-full flex items-center justify-center text-mute">
            <BookOpen size={36} weight="duotone" />
          </div>
        )}
        {paper.is_free ? (
          <span className="absolute top-3 right-3 tag !bg-forest !text-white !border-forest inline-flex items-center gap-1">
            <Sparkle size={10} weight="fill" /> Free
          </span>
        ) : (
          <span className="absolute top-3 right-3 tag !bg-ochre/90 !text-ink !border-ochre-dark inline-flex items-center gap-1">
            <Lock size={10} weight="fill" /> Grower
          </span>
        )}
        {paper.category && (
          <span className="absolute top-3 left-3 tag bg-white/90 backdrop-blur capitalize">
            {CATEGORY_LABEL[paper.category] || paper.category}
          </span>
        )}
      </div>
      <div className="p-5">
        <div className="font-mono-data text-mute text-xs flex items-center gap-2">
          <span>{fmtDate(paper.created_at)}</span>
          {mins && (
            <>
              <span aria-hidden>·</span>
              <span>{mins} min read</span>
            </>
          )}
        </div>
        <h3 className="font-display text-2xl text-ink mt-1 leading-snug">{paper.title}</h3>
        {paper.abstract && (
          <p className="text-mute mt-2 text-sm leading-relaxed line-clamp-3">{paper.abstract}</p>
        )}
        <div className="mt-4 flex items-center justify-between">
          <div className="flex flex-wrap gap-1.5">
            {(paper.tags || []).slice(0, 3).map((t, i) => (
              <span key={i} className="tag severity-mild">{t}</span>
            ))}
          </div>
          <span className="text-forest text-sm inline-flex items-center gap-1 group-hover:gap-2 transition-all">
            Read <ArrowRight size={14} weight="bold" />
          </span>
        </div>
      </div>
    </Link>
  );
}
